import { faSearch } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Slider,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material"
import { styled } from "@mui/material/styles"
import { DatePicker } from "@mui/x-date-pickers"
import { t } from "i18next"
import moment from "moment"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { StyledSlider, StyledSliderWrapper } from "./ClpTable.styled"
import {
  StyledTableColumnSelect,
  StyledTableDefaultTextfield,
  StyledTableFormControl,
  StyledTableSearchTextfield
} from "shared/styled/General"

export function GlobalFilter({ initialValue = "", globalFilter = null, setGlobalFilter, debounce = 500 }) {
  const [value, setValue] = useState(globalFilter ?? initialValue)
  const { t } = useTranslation()

  React.useEffect(() => {
    if (globalFilter !== value) {
      setValue(globalFilter ?? "")
    }
  }, [globalFilter])

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      setGlobalFilter(value || undefined)
    }, debounce)

    return () => clearTimeout(timeout)
  }, [value])

  return (
    <StyledTableSearchTextfield
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <FontAwesomeIcon icon={faSearch} />
          </InputAdornment>
        )
      }}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      placeholder={t("global.search", "Search")}
      size="small"
    />
  )
}

export function DefaultColumnFilter({ column: { getFilterValue, columnDef, setFilterValue } }) {
  return (
    <>
      <StyledTableDefaultTextfield
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <FontAwesomeIcon icon={faSearch} />
            </InputAdornment>
          )
        }}
        value={getFilterValue() || ""}
        onChange={(e) => {
          setFilterValue(e.target.value || undefined)
        }}
        label={`${columnDef.header}`}
        variant="outlined"
        size="small"
      />
    </>
  )
}

export function SelectColumnFilter({ column }) {
  const { getFilterValue, setFilterValue, columnDef } = column
  const { t } = useTranslation()

  const options = useMemo(() => {
    if (columnDef?.meta?.filterOptions !== undefined) {
      return columnDef.meta.filterOptions.sort((a, b) => a?.name?.localeCompare(b?.name))
    }

    const unsortedOptions = Array.from(column.getFacetedUniqueValues().keys()).map((x) => {
      return { value: x, name: x }
    })

    return unsortedOptions.sort((a, b) => a?.name?.localeCompare(b?.name))
  }, [column, columnDef?.meta?.filterOptions])

  useEffect(() => {
    setFilterValue(getFilterValue())
  }, [getFilterValue()])

  const handleChange = (event) => {
    setFilterValue(String(event?.target?.value.toString()))
  }

  return (
    <StyledTableFormControl size="small" fullWidth>
      <InputLabel htmlFor={columnDef.header} id={`label${columnDef.header}`}>
        {columnDef.header}
      </InputLabel>

      <StyledTableColumnSelect
        labelId={`label${columnDef.header}`}
        autoWidth
        value={getFilterValue() || ""}
        label={columnDef.header}
        id={columnDef.header}
        onChange={handleChange}>
        <MenuItem value="">
          <em>{t("global.show_all", "Show all")}</em>
        </MenuItem>
        {options.map((option, i) => (
          <MenuItem key={i} value={option.value}>
            {option.name}
          </MenuItem>
        ))}
      </StyledTableColumnSelect>
    </StyledTableFormControl>
  )
}

export const MultiSelectFilter = ({ column }) => {
  const { getFilterValue, setFilterValue, columnDef } = column

  useEffect(() => {
    if (columnDef.meta.defaultValue !== undefined) {
      setFilterValue(columnDef.meta.defaultValue)
    }
  }, [])

  const options = React.useMemo(() => {
    const availableColumnValues = Array.from(column.getFacetedUniqueValues()?.keys() || [])

    if (columnDef?.meta?.filterOptions) {
      return columnDef.meta.filterOptions.sort((a, b) => a.name.localeCompare(b.name))
    }

    return availableColumnValues
      .filter((x) => x !== null && x !== undefined && x !== "")
      .map((x) => ({ value: x, name: x }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [column, columnDef?.meta?.filterOptions])

  const handleClick = (event) => {
    const { value } = event.target
    setFilterValue(value.length > 0 ? value : "")
  }

  const activeOption = (option) => {
    return getFilterValue() !== undefined
      ? getFilterValue().findIndex((x) => x === option.value) === -1
      : true
  }

  return (
    <StyledTableFormControl size="small" fullWidth>
      <InputLabel id="filter-checkbox-label">{columnDef.meta?.filterHeader ?? columnDef.header}</InputLabel>
      <Select
        labelId={columnDef.header}
        label={columnDef.meta?.filterHeader ?? columnDef.header}
        id={columnDef.header}
        multiple
        value={getFilterValue() ? getFilterValue() : []}
        onChange={handleClick}
        renderValue={(selected) => {
          return selected
            .map((s) => options?.find((f) => f.value === s)?.name)
            .filter((name) => name !== undefined)
            .join(", ")
        }}>
        {options?.map((option, index) => (
          <MenuItem key={index} value={option.value}>
            <Checkbox checked={!activeOption(option)} />
            <ListItemText primary={option.name} />
          </MenuItem>
        ))}
      </Select>
    </StyledTableFormControl>
  )
}

export const ServerSideMultiSelectFilter = ({ column }) => {
  const { getFilterValue, setFilterValue, columnDef } = column
  const { t } = useTranslation()

  const options = React.useMemo(() => {
    const configuredFilterOptions = columnDef?.meta?.filterOptions || []
    return configuredFilterOptions.sort((a, b) => a.name.localeCompare(b.name))
  }, [columnDef?.meta?.filterOptions])

  useEffect(() => {
    if (columnDef.meta?.defaultValue !== undefined) {
      setFilterValue(columnDef.meta.defaultValue)
    }
  }, [columnDef.meta?.defaultValue, setFilterValue])

  const handleClick = (event) => {
    const { value } = event.target
    setFilterValue(value.length > 0 ? value : "")
  }

  const activeOption = (option) => {
    return getFilterValue() !== undefined
      ? getFilterValue().findIndex((x) => String(x) === String(option.value)) === -1
      : true
  }

  return (
    <StyledTableFormControl size="small" fullWidth>
      <InputLabel id="filter-checkbox-label">{columnDef.header}</InputLabel>
      <StyledTableColumnSelect
        labelId={columnDef.header}
        label={columnDef.header}
        id={columnDef.header}
        multiple
        value={getFilterValue() ? getFilterValue() : []}
        onChange={handleClick}
        renderValue={(selected) => {
          return selected
            .map((s) => options?.find((f) => f.value === s)?.name)
            .filter((name) => name !== undefined)
            .join(", ")
        }}>
        {options?.map((option, index) => (
          <MenuItem key={index} value={option.value}>
            <Checkbox checked={!activeOption(option)} />
            <ListItemText primary={option.name} />
          </MenuItem>
        ))}
      </StyledTableColumnSelect>
    </StyledTableFormControl>
  )
}

export function SliderColumnFilter({ column: { getFilterValue, setFilterValue, preFilteredRows, id } }) {
  const [min, max] = useMemo(() => {
    let min = preFilteredRows.length ? preFilteredRows[0].values[id] : 0
    let max = preFilteredRows.length ? preFilteredRows[0].values[id] : 0
    preFilteredRows.forEach((row) => {
      min = Math.min(row.values[id], min)
      max = Math.max(row.values[id], max)
    })
    return [min, max]
  }, [id, preFilteredRows])

  return (
    <>
      <Slider
        type="range"
        step={1}
        marks
        min={min}
        max={max}
        value={getFilterValue() || min}
        onChange={(e) => {
          setFilterValue(parseInt(e.target.value, 10))
        }}
      />
      <button onClick={() => setFilterValue(undefined)}>{t("global.of", "Of")}</button>
    </>
  )
}

export function NumberRangeColumnFilter({ column, debounce = 500 }) {
  const { setFilterValue, columnDef, getFilterValue, getFacetedMinMaxValues } = column
  const [min, setMin] = useState(0)
  const [max, setMax] = useState(0)
  const [value, setValue] = useState([])
  const theme = useTheme()
  const isPhone = useMediaQuery(theme.breakpoints.only("xs"))

  useEffect(() => {
    if (!getFacetedMinMaxValues()) return
    const [lowestMin, highestMax] = getFacetedMinMaxValues()
    setMin(lowestMin)
    setMax(highestMax)
    setFilterValue([lowestMin, highestMax])
    setValue([lowestMin, highestMax])
  }, [getFacetedMinMaxValues])

  useEffect(() => {
    const timeout = setTimeout(() => {
      setValue(value || [])
      setFilterValue(value || [])
    }, debounce)

    return () => clearTimeout(timeout)
  }, [value])

  useEffect(() => {
    if (getFilterValue() === undefined) {
      setFilterValue([min, max])
      setValue([min, max])
    } else if (getFilterValue().length === 1) {
      if (getFilterValue()[0] === value[0]) {
        setFilterValue([getFilterValue()[0], max])
        setValue([getFilterValue()[0], max])
      } else {
        setFilterValue([min, getFilterValue()[0]])
        setValue([min, getFilterValue()[0]])
      }
    }
  }, [getFilterValue()])

  const handleChange = (event, newValue) => {
    setValue(newValue)
  }

  const marks = [
    {
      value: min,
      label: `${min}`
    },
    {
      value: max,
      label: `${max}`
    }
  ]

  return (
    <StyledSliderWrapper>
      <Typography id="range-slider">{columnDef.header}</Typography>
      <Grid container>
        <Grid item xs={12} sx={{ padding: "0 6px" }}>
          <StyledSlider
            isPhone={isPhone}
            value={value || []}
            onChange={handleChange}
            valueLabelDisplay="auto"
            aria-labelledby="range-slider"
            min={min}
            max={max}
            marks={isPhone && marks}
          />
        </Grid>
      </Grid>
    </StyledSliderWrapper>
  )
}

export function SwitchColumnFilter({ column: { setFilterValue, columnDef, getFilterValue } }) {
  const [value, setValue] = useState(columnDef.defaultValue === "Ja")

  useEffect(() => {
    if (getFilterValue() === undefined) {
      setValue()
    }
  }, [getFilterValue()])

  const handleChange = (event) => {
    const newValue = event.target.checked
    setValue(newValue)
    setFilterValue(newValue ? "Ja" : "Nej")
  }

  return (
    <FormControl size="small">
      <FormControlLabel
        control={<Switch onChange={handleChange} id="switch" checked={value} />}
        label={columnDef.header}
      />
    </FormControl>
  )
}

export function DatePickerColumnFilter({
  column: { defaultValue, getFilterValue, setFilterValue, columnDef }
}) {
  useEffect(() => {
    if (getFilterValue() === undefined) {
      setFilterValue()
      return
    }
    setFilterValue(moment(getFilterValue()).format("YYYY-MM-DD"))
  }, [getFilterValue()])

  const handleDateChange = (date) => {
    setFilterValue(moment(date).isValid() ? moment(date).format("YYYY-MM-DD") : null)
  }

  return (
    <DatePicker
      slotProps={{
        textField: {
          size: "small",
          sx: {
            width: "100%"
          }
        },
        actionBar: {
          actions: ["clear"],
          clearLabel: "Clear"
        }
      }}
      value={getFilterValue() ? moment.utc(getFilterValue()) : null}
      onChange={handleDateChange}
      label={columnDef.header}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Välj datum"
          id={"date" + columnDef.header}
          value={getFilterValue() || ""}
          onChange={handleDateChange}
        />
      )}
      id={"date" + columnDef.header}
    />
  )
}
